/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import { debounce } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';

import Input from 'components/Input.jsx';
import Glyph from 'components/Glyph.jsx';
import KeyConstants from 'constants/KeyConstants';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import ScrollableArea from 'components/ScrollableArea.jsx';

import './styles/AsyncSelect.scss';

type Props = {
  // =============
  // = callbacks =
  // =============

  // The function that will be called to load results.
  //   Must take a query string and a callback function.
  //   The search callback must call the callback with a
  //     promise that returns the rendered results to display
  //     in this AsyncSelect
  searchCallback: Function,

  // Called when the user clicks the x to clear this AsyncSelect
  onClear?: Function,

  // Called when the user selections an option
  onSelect?: Function,

  // ===========
  // = options =
  // ===========

  // The initial item to show in this AsyncSelect
  defaultItem?: React$Element<*>, // eslint-disable-line react/no-unused-prop-types

  // A classname for the select itself
  className?: string,

  // A classname for the results layer
  layerClassName?: string,

  // Whether the items array should be cleared any time the input changes
  clearOnInputChange?: boolean,

  // The number of characters required before searchCallback gets called
  queryMinSize?: number,

  // What to show when the query is shorter than queryMinSize
  queryTooShort?: string,

  // Items that will always be included in the results
  permanentItems?: React$Element<*>[],

  // Items that will be displayed if there is no query
  initialItems?: React$Element<*>[],

  // A millisecond delay before calling searchCallback. This is used so that
  //   when a user types fast it doesn't make a bunch of useless calls
  inputDelay?: number,

  // What to show when there's no results
  noResults: React$Element<*>,

  // What text to show when there's nothing in the input
  placeholder?: string,

  // Whether results should continue to load if scrolled to bottom
  infiniteLoad?: boolean,

  // What to show when there's no query
  noQuery?: string,
};
/*
 * Component to perform async searches.
 * To do so you must register a callback to the component
 * that will be called when a search is need. This registered
 * callback will have two parameters, the search query and a
 * callback. The callback must be called with a promise that
 * returns the search results as AsyncSelectItem.
 *
 * renderSomething(something) {
 *   return (
 *     <AsyncSelectItem id={something.id} title={something.getName()}>
 *       <span>{something.getFirstname()}</span>
 *     </AsyncSelectItem>
 *   );
 * },
 *
 * onSearchCallback(input, callback) {
 *   callback(Something.search(input).then((somethings) => {
 *     return somethings.map(this.renderSomething);
 *   }));
 * },
 *
 * onSelect(id) {
 *   console.log('item selected', id);
 * }
 *
 * render: function() {
 *   return (
 *     <AsyncSelect
 *       searchCallback={this.onSearchCallback}
 *       onSelect={this.onSelect} />
 *   );
 * }
 *
 */
class AsyncSelect extends React.Component {
  props: Props;

  static defaultProps = {
    queryMinSize: 1,
    inputDelay: 0,
    permanentItems: [],
    noResults: (<div className="AsyncSelect-searchResults-none">No results</div>),
    placeholder: '',
    infiniteLoad: false,
  };

  state = {
    searchString: '',
    items: [],
    hasSelected: false,
    highlight: 0,
    pendingQueries: 0,
    userTyping: false,
    lastSelectedItem: (null: ?React$Element<*>),
    showResults: false,
  };

  componentDidMount() {
    this._width = ReactDOM.findDOMNode(this.refs.select).offsetWidth;
    if (this.props.defaultItem) {
      this.setSelectedItem(this.props.defaultItem);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.defaultItem) {
      this.setSelectedItem(nextProps.defaultItem);
    }
  }

  _width: ?number = null;
  _isMouseScrolling: boolean = false;

  onBlur = () => {
    let newSearchString = '';
    if (this.state.lastSelectedItem) {
      newSearchString = this.state.lastSelectedItem.props.title;
    }
    this.clearResults();
    this.setState({
      searchString: newSearchString,
      showResults: false,
    });
  };

  onFocus = () => {
    this.setState({ showResults: true });
  };

  onInputChange = () => {
    const value = ReactDOM.findDOMNode(this.refs.myInput).value;

    this.setState({ searchString: value });

    if (value.length >= this.props.queryMinSize) {
      this.setState({
        showResults: true,
        userTyping: true,
      });
      this.callSearchCallback(value);
    } else if (value.length === 0) {
      this.clearResults();
    }

    if (this.props.clearOnInputChange) {
      if (this.props.onClear) {
        this.props.onClear();
      }

      this.clearResults();
    }
  };

  onInputFocus = () => {
    if (this.state.lastSelectedItem &&
        this.state.searchString === this.state.lastSelectedItem.props.title) {
      this.clearInput();
      this.clearResults();
    }
  };

  onResultClick = (item: React$Element<*>) => {
    this.onResultSelected(item);
  };

  onResultSelected = (item: React$Element<*>) => {
    if (!this.isItemPermanent(item)) {
      this.setState({
        searchString: item.props.title,
        lastSelectedItem: item,
        hasSelected: true,
      });
    }

    if (this.props.onSelect) {
      this.props.onSelect(item);
    }

    this.clearResults();
    this.setState({
      showResults: false,
    });
  };

  getSearchString = () => {
    return this.state.searchString;
  };

  setSelectedItem = (item: React$Element<*>) => {
    const input = ReactDOM.findDOMNode(this.refs.myInput);
    input.value = item.props.title;
    this.setState({
      searchString: item.props.title,
      lastSelectedItem: item,
      hasSelected: true,
    });
  };

  callSearchCallback = (value: string) => {
    const searchAndDisplay = (query) => this.props.searchCallback(query, this.displayAsyncResults);
    const debouncedSearchCallback = debounce(searchAndDisplay, this.props.inputDelay);
    debouncedSearchCallback(value);
  };

  clearInput = () => {
    this.setState({
      searchString: '',
    });
  };

  clearResults = () => {
    this.setState({
      highlight: 0,
      items: [],
    });
  };

  displayAsyncResults = (promise: Promise<*>) => {
    this.setState({
      userTyping: false,
      pendingQueries: this.state.pendingQueries + 1,
    });
    promise.then(items => {
      this.setState({
        pendingQueries: this.state.pendingQueries - 1,
        items,
      });
    }).catch(err => console.error('Display async results failure', err));
  };

  handleInputKeyDown = (event: SyntheticKeyboardEvent) => {
    const code = event.keyCode;
    switch (code) {
      case KeyConstants.ENTER: {
        const item = this.state.items[this.state.highlight];
        if (item) {
          this.onResultSelected(item);
        }
        break;
      }
      case KeyConstants.UP:
      case KeyConstants.DOWN: {
        let { highlight } = this.state;
        if (code === KeyConstants.UP) {
          highlight = highlight === 0 ? this.state.items.length - 1 : highlight - 1;
        } else {
          highlight = highlight === (this.state.items.length - 1) ? 0 : highlight + 1;
        }
        this.setState({ highlight }, () => {
          const searchItemNode = ReactDOM.findDOMNode(this.refs[`item-${highlight}`]);
          this.refs.scrollArea.scrollIntoView(searchItemNode);
        });

        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  handleResultMouseOver = (index: number) => {
    if (this._isMouseScrolling) {
      this._isMouseScrolling = false;
      return;
    }
    this.setState({ highlight: index });
    if (this.props.infiniteLoad &&
      this.isScrolledToBottom() &&
      // $FlowIssue defaultProps
      this.state.searchString.length >= this.props.queryMinSize) {
      this.callSearchCallback(this.state.searchString);
    }
  };

  hasItems = () => {
    return this.state.items && this.state.items.length > 0;
  };

  isItemPermanent = (item: React$Element<*>) => {
    const { permanentItems } = this.props;
    // $FlowFixMe defaultProps
    return permanentItems.find(result => {
      return result.props.id === item.props.id;
    });
  };

  isScrolledToBottom = () => {
    return this.refs.scrollArea.isScrolledToBottom(150);
  };

  focus = () => {
    this.refs.myInput.focus();
  };

  waitingOnQueries = () => {
    return this.state.userTyping === true || this.state.pendingQueries > 0;
  };

  renderLoadingIndicator() {
    if (this.waitingOnQueries()) {
      return (
        <div className="AsyncSelect-searchLoading">
          <LoadingIndicator size="small" />
        </div>
      );
    } else {
      return null;
    }
  }

  renderNoResultsOrQuery() {
    const query = this.state.searchString;

    if (this.state.items.length === 0 && !this.waitingOnQueries()) {
      if (query === '') {
        return this.props.noQuery && <div className="AsyncSelect-noQuery">{this.props.noQuery}</div>;
      } else if (query.length < this.props.queryMinSize && this.props.queryTooShort) {
        return <div className="AsyncSelect-noQuery">{this.props.queryTooShort}</div>;
      } else {
        return this.props.noResults;
      }
    }

    return null;
  }

  renderPermanent = (item: React$Element<*>, index: number) => {
    return (
      <div
        className={cx('AsyncSelect-searchResult-permanent')}
        key={item.props.id || `permanent-${index}`}
        onMouseDown={() => this.onResultClick(item)}>
        {item}
      </div>
    );
  }

  renderResult = (item: React$Element<*>, index: number) => {
    return (
      <div
        className={cx('AsyncSelect-searchResult', {
          highlight: index === this.state.highlight,
        })}
        ref={`item-${index}`}
        key={item.props.id || `item-${index}`}
        onMouseOver={() => this.handleResultMouseOver(index)}
        onMouseDown={() => this.onResultClick(item)}>
        {item}
      </div>
    );
  }

  renderResults(layerClassName?: ?string) {
    if (!this.state.showResults) {
      return null;
    }

    if (!this.props.noQuery && !this.props.initialItems && this.state.searchString === '') {
      return null;
    }

    let permanentItems;
    if (this.props.permanentItems) {
      permanentItems = (
        <div>
          {this.props.permanentItems.map(this.renderPermanent)}
        </div>
      );
    }

    let resultingItems;
    if (this.hasItems() || this.waitingOnQueries()) {
      resultingItems = (
        <ScrollableArea
          className="AsyncSelect-list"
          ref="scrollArea"
          useBodyChildrenHeight>
          {this.state.items.map(this.renderResult)}
          {this.renderLoadingIndicator()}
        </ScrollableArea>
      );
    } else if (this.props.initialItems && !this.state.searchString) {
      resultingItems = (
        <ScrollableArea
          className="AsyncSelect-list"
          ref="scrollArea"
          useBodyChildrenHeight>
          {this.props.initialItems.map(this.renderResult)}
        </ScrollableArea>
      );
    }

    return (
      <div className="AsyncSelect-resultsWrapper">
        <div
          className={cx('AsyncSelect-searchResults', layerClassName)}
          style={{
            width: this._width,
          }}>
          {this.renderNoResultsOrQuery()}
          {resultingItems}
          <div className="AsyncSelect-searchResults-permanent">
            {permanentItems}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { className, layerClassName, ...others } = this.props;

    return (
      <div
        ref="select"
        className={cx('AsyncSelect', className)}
        onFocus={this.onFocus}
        onBlur={this.onBlur}>
        <Input
          {...others}
          ref="myInput"
          className="AsyncSelect-input"
          placeholder={this.props.placeholder}
          onKeyDown={this.handleInputKeyDown}
          onChange={this.onInputChange}
          onFocus={this.onInputFocus}
          value={this.state.searchString || ''}
        />
        <Glyph className="AsyncSelect-glyph search" name="search" size="medium" />
        {this.renderResults(layerClassName)}
      </div>
    );
  }
}

export default AsyncSelect;



// WEBPACK FOOTER //
// ./src/components/AsyncSelect.jsx